import { t } from '@lingui/core/macro';
import * as React from 'react';

import { BaseField, BaseFieldProps, FieldConverter } from '../BaseField';

export interface FieldNumericProps extends Omit<BaseFieldProps<number | null>, 'converter'> {
    /**
     * Reads "1,000" and "1.000" as 1000. Without it or `isRate`, the field can't tell them from 1 and asks the
     * user to type the number another way.
     */
    isInteger?: boolean;
    /**
     * Reads "1,125" and "1.125" as 1.125. For unit prices, exchange rates and percentages, which often have three
     * decimals and rarely reach 1,000.
     */
    isRate?: boolean;
}

export const FieldNumeric = React.forwardRef<HTMLDivElement, FieldNumericProps>(
    ({ isInteger, isRate, ...props }, ref) => {
        const converter = isInteger ? fieldConverterInteger : isRate ? fieldConverterRate : fieldConverterNumber;
        return <BaseField ref={ref} converter={converter} {...props} />;
    },
);

type NumberText = { ok: true; value: string | null } | { ok: false; message: string };

/** How a field reads text like "1,125", which is 1125 in one style and 1.125 in the other. */
type AmbiguousReading = 'thousands' | 'decimal' | 'ask';

// One comma or point followed by exactly three digits, like "1,000" or "12.500": thousands in one
// style, a decimal point in the other.
const AMBIGUOUS_NUMBER = /^([+-]?)([1-9]\d{0,2})([.,])(\d{3})$/;

/**
 * Reads a number typed or pasted in English or German style ("1,234.5", "1.234,5", "1 234,5", "1'234.5")
 * and returns it as plain text, like "1234.5".
 *
 * Text like "1,000" is ambiguous, and a wrong guess stores a number a thousand times too small or too large.
 * Integer fields read it as thousands, rate fields as a decimal, and other fields ask the user.
 */
function readNumberText(text: string, ambiguousReading: AmbiguousReading): NumberText {
    const trimmed = text.trim();
    if (trimmed === '') {
        return { ok: true, value: null };
    }

    const ambiguous = AMBIGUOUS_NUMBER.exec(trimmed);
    if (ambiguous) {
        const [, sign, integerDigits, mark, fractionDigits] = ambiguous;
        const whole = `${sign}${integerDigits}${fractionDigits}`;
        if (ambiguousReading === 'thousands') {
            return { ok: true, value: whole };
        }
        if (ambiguousReading === 'decimal') {
            return { ok: true, value: `${sign}${integerDigits}.${fractionDigits}` };
        }
        const decimalText = toUnambiguousNumberText(`${sign}${Number(`${integerDigits}.${fractionDigits}`)}`);
        const decimal = decimalText.replace('.', mark);
        return { ok: false, message: t`Type ${whole} or ${decimal} to make clear which number you mean.` };
    }

    const match = /^([+-]?)([\d.,\s'’]+)$/.exec(trimmed);
    if (!match) {
        // Forms JavaScript can read, like "1e-7".
        return isNaN(Number(trimmed)) ? { ok: false, message: t`Enter a number` } : { ok: true, value: trimmed };
    }

    // With both marks, the last one is the decimal point. A single comma or point is one too, since the
    // ambiguous case is handled above. A mark that appears more than once only groups thousands.
    const [, sign, body] = match;
    const lastMarkIndex = Math.max(body.lastIndexOf('.'), body.lastIndexOf(','));
    const isDecimalMark = lastMarkIndex !== -1 && body.indexOf(body.charAt(lastMarkIndex)) === lastMarkIndex;
    const integerPart = isDecimalMark ? body.slice(0, lastMarkIndex) : body;
    const fraction = isDecimalMark ? body.slice(lastMarkIndex + 1) : '';

    const integerDigits = readGroupedDigits(integerPart);
    if (integerDigits === null || !/^\d*$/.test(fraction)) {
        return { ok: false, message: t`Check the separators. Thousands come in groups of three digits.` };
    }
    if (integerDigits === '' && fraction === '') {
        return { ok: false, message: t`Enter a number` };
    }
    return { ok: true, value: `${sign}${integerDigits || '0'}${fraction ? `.${fraction}` : ''}` };
}

/** Removes the thousands separators from "1,234,567" or "1 234 567". Returns null unless they group by three. */
function readGroupedDigits(text: string): string | null {
    const normalized = text.replace(/\s/g, ' ').replace(/’/g, "'");
    const separators = new Set(normalized.replace(/\d/g, ''));
    if (separators.size === 0) {
        return normalized;
    }
    const [first, ...rest] = normalized.split(/\D/);
    if (separators.size > 1 || !/^\d{1,3}$/.test(first) || !rest.every((group) => /^\d{3}$/.test(group))) {
        return null;
    }
    return normalized.replace(/\D/g, '');
}

/** Adds a zero to text like "1.125", which the field would otherwise reject as ambiguous. */
function toUnambiguousNumberText(text: string): string {
    return AMBIGUOUS_NUMBER.test(text) ? `${text}0` : text;
}

function createNumericStringConverter(ambiguousReading: AmbiguousReading): FieldConverter<string | null> {
    return {
        parseValue: (text: string) => {
            const result = readNumberText(text, ambiguousReading);
            // Report null for unreadable text, so the field doesn't keep a number that was read from part of it.
            return result.ok ? result : { ok: false, value: null, message: result.message };
        },
        formatValue: (value) => {
            const text = value ?? '';
            // A rate field reads "1.125" back as 1.125, so it needs no extra zero.
            return ambiguousReading === 'decimal' ? text : toUnambiguousNumberText(text);
        },
    };
}

function createNumberConverter(stringConverter: FieldConverter<string | null>): FieldConverter<number | null> {
    return {
        parseValue: (text: string) => {
            const result = stringConverter.parseValue(text);
            if (!result.ok) {
                return { ok: false, value: null, message: result.message };
            }
            return { ok: true, value: result.value === null ? null : Number(result.value) };
        },
        formatValue: (value) => {
            return stringConverter.formatValue(String(value ?? ''));
        },
    };
}

export const fieldConverterString = createNumericStringConverter('ask');

export const fieldConverterIntegerString = createNumericStringConverter('thousands');

export const fieldConverterRateString = createNumericStringConverter('decimal');

export const fieldConverterNumber = createNumberConverter(fieldConverterString);

export const fieldConverterInteger = createNumberConverter(fieldConverterIntegerString);

export const fieldConverterRate = createNumberConverter(fieldConverterRateString);

/** A percentage is a rate, so "12.500" is 12.5 %. */
export const fieldConverterPercent: FieldConverter<number | null> = {
    parseValue: (text: string) => {
        const result = fieldConverterRate.parseValue(text);
        if (!result.ok || result.value === null) {
            return result;
        }
        return { ok: true, value: result.value / 100 };
    },
    formatValue: (value) => {
        if (value === null) {
            return '';
        }
        return fieldConverterRate.formatValue(value * 100);
    },
};
